/**
 * PlyxInstallFAB — small floating Install button always visible on Plyx
 * routes (until the app is installed). Lets users open the install
 * prompt whenever they want without depending on the auto-pill.
 *
 * Positioned above the bottom navigation so it doesn't overlap the
 * tabs, with a gradient pill so it's instantly recognisable.
 *
 * Hidden on:
 *   • Auth pages (login/register) where the user is mid-form
 *   • Once the app is in standalone display mode (already installed)
 *   • Full-screen live broadcaster surfaces (same exclusions as the
 *     bottom-nav so we never block creator tools)
 */
import React from "react";
import { useLocation } from "react-router-dom";
import { Download } from "lucide-react";
import useInstallPrompt from "@/hooks/useInstallPrompt";
import { trackPlyxInstall } from "@/lib/trackPlyxInstall";

// Top-level appinstalled tap — fires whether the install came from
// the FAB, the auto-pill, the browser address bar, or the OS itself.
// Wired once at module-load so it survives FAB unmount/remount.
if (typeof window !== "undefined" && !window.__plyxInstallTracked) {
  window.addEventListener("appinstalled", () => {
    trackPlyxInstall("os_installed");
  });
  window.__plyxInstallTracked = true;
}

const HIDDEN_PREFIXES = [
  "/plyx/login",
  "/plyx/register",
  "/plyx/forgot",
  "/plyx/reset",
  "/plyx/admin/login",
  "/plyx/live/",
];

const PlyxInstallFAB = () => {
  const { installed } = useInstallPrompt();
  const { pathname } = useLocation();

  // Always show on Plyx routes until the app is actually installed —
  // the prompt component handles every browser quirk (deferred event,
  // iOS Safari instructions, unsupported fallback toast) so the FAB
  // doesn't need to gate itself on canInstall.
  if (installed) return null;
  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  const onTap = () => {
    trackPlyxInstall("fab_tapped");
    // Surface the existing PlyxInstallPrompt — it owns the actual
    // beforeinstallprompt handle and the iOS instructions modal. We
    // dispatch a custom event the prompt component is already wired
    // to listen for.
    window.dispatchEvent(new Event("plyx-show-install"));
  };

  return (
    <button
      type="button"
      onClick={onTap}
      data-testid="plyx-install-fab"
      aria-label="Install Plyx"
      // Anchored just above the 56px bottom nav (sm:hidden — desktop
      // doesn't have the dock so we drop the offset). z-50 keeps it
      // clear of the install pill (which is z-60) so dismissing the
      // pill still leaves the FAB tappable.
      className="fixed bottom-[68px] sm:bottom-5 right-3 z-[50] inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full shadow-lg shadow-black/40 text-black text-xs font-semibold animate-plyx-fab-bob"
      style={{
        background: "#E85D4E",
      }}
    >
      <Download className="w-3.5 h-3.5" strokeWidth={2.4} />
      Install
    </button>
  );
};

export default PlyxInstallFAB;
